import { UserType } from '@/lib/api/common/interfaces/user.interface'
import { getUser } from '@/lib/api/users/users-api'
import { useAppDispatch, useAuth } from '@/store/hooks'
import { setCredentials } from '@/store/slices/authSlice'
import { useCallback, useEffect, useRef } from 'react'
import { isAiUserType } from '@/lib/auth/restricted-viewer'

const RELOAD_KEY = 'atendimento_user_reloaded'

if (typeof window !== 'undefined') {
    const navigationEntries = performance.getEntriesByType(
        'navigation'
    ) as PerformanceNavigationTiming[]
    const isReload =
        navigationEntries.length > 0 && navigationEntries[0].type === 'reload'

    if (isReload) {
        sessionStorage.removeItem(RELOAD_KEY)
    }

    window.addEventListener('beforeunload', () => {
        sessionStorage.removeItem(RELOAD_KEY)
    })
}

export function useReloadUser() {

    const dispatch = useAppDispatch()
    const { isAuthenticated, user, token, refreshToken } = useAuth()
    const hasReloadedRef = useRef(false)

    const doReload = useCallback(async () => {
        if (!isAuthenticated || !user?.id || !token) {
            return
        }

        try {
            const response = await getUser(user.id)

            if (response.success && response.result) {
                const updatedUser = response.result

                // Preservar a company atual se já existir (em caso de switch company)
                // A company no banco pode ser diferente da company no JWT após switch
                const isCompanySwitched =
                    user.companyId &&
                    updatedUser.companyId &&
                    user.companyId !== updatedUser.companyId

                const mergedCompany = isCompanySwitched
                    ? user.company
                        ? {
                              ...user.company,
                              organization: updatedUser.company?.organization ?? user.company.organization,
                          }
                        : updatedUser.company
                    : updatedUser.company || user.company

                const mergedUser = {
                    ...updatedUser,
                    companyId: user.companyId || updatedUser.companyId,
                    company: mergedCompany,
                }

                dispatch(
                    setCredentials({
                        user: mergedUser,
                        token: token,
                        refreshToken: refreshToken || '',
                        isAuthenticated: true,
                        isMaster:
                            mergedUser.userType === UserType.MASTER &&
                            mergedUser.role?.name === 'master',
                        isCompany:
                            mergedUser.userType === UserType.COMPANY &&
                            mergedUser.role?.name === 'company',
                        isCustom: mergedUser.userType === UserType.CUSTOM,
                        isEmployee:
                            (mergedUser.userType === UserType.EMPLOYEE &&
                                mergedUser.role?.name === 'employee') ||
                            isAiUserType(mergedUser.userType),
                        isPartner:
                            mergedUser.userType === UserType.PARTNER,
                        isProfessional:
                            mergedUser.userType === UserType.PROFESSIONAL &&
                            mergedUser.role?.name === 'professional',
                        accessibleCompanies: mergedUser.accessibleCompanies || [],
                    })
                )

                if (process.env.NODE_ENV === 'development') {
                    console.log(
                        '[UserReload] Dados do usuário recarregados do servidor'
                    )
                }
            }
        } catch (error) {
            console.error(
                '[UserReload] Erro ao recarregar dados do usuário:',
                error
            )
        }
    }, [isAuthenticated, user?.id, user?.companyId, token, refreshToken, dispatch])

    /**
     * Volta do checkout público (`/pay/:token`) pelo botão "voltar" do navegador:
     * o Chrome restaura a página do bfcache, o React não remonta e o Redux
     * continua com a assinatura de antes do pagamento — o aviso de "assinatura
     * suspensa" ficava na tela até um F5 manual. `pageshow` com `persisted` é o
     * único sinal desse retorno.
     */
    useEffect(() => {
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                sessionStorage.removeItem(RELOAD_KEY)
                void doReload()
            }
        }

        window.addEventListener('pageshow', handlePageShow)
        return () => window.removeEventListener('pageshow', handlePageShow)
    }, [doReload])

    useEffect(() => {
        if (hasReloadedRef.current) {
            return
        }

        const hasReloaded = sessionStorage.getItem(RELOAD_KEY) === 'true'

        if (hasReloaded) {
            hasReloadedRef.current = true
            return
        }

        hasReloadedRef.current = true
        sessionStorage.setItem(RELOAD_KEY, 'true')

        doReload()
    }, [doReload])

    return { reloadUser: doReload }
}
